/**
 * MetricsPanel — renders a single dashboard panel.
 *
 * Fetches data from the telemetry-service query proxy, substitutes
 * $device_id / $fleet_id variables, and renders the appropriate chart type.
 */
import React from 'react'
import TimeseriesChart from './TimeseriesChart'
import { useQueryRange, useLogQuery } from '@/api/telemetry'
import type { Panel, LogEntry } from '@/types'

interface MetricsPanelProps {
  panel: Panel
  tenantId: string
  variables?: Record<string, string>   // e.g. { device_id: 'xxx', fleet_id: 'yyy' }
  timeFrom?: string    // e.g. 'now-6h'
  timeTo?: string      // e.g. 'now'
  step?: string        // e.g. '60s'
  height?: number
  /** Admin toolbar: edit / delete callbacks */
  onEdit?: () => void
  onDelete?: () => void
}

function substituteVars(query: string, vars: Record<string, string>): string {
  let q = query
  for (const [k, v] of Object.entries(vars)) {
    q = q.replaceAll(`$${k}`, v)
  }
  return q
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 600,
  marginBottom: 6,
}

export default function MetricsPanel({
  panel,
  tenantId,
  variables = {},
  timeFrom = 'now-6h',
  timeTo = 'now',
  step,
  height = 200,
  onEdit,
  onDelete,
}: MetricsPanelProps) {
  const query = substituteVars(panel.query, variables)

  const promResult = useQueryRange(
    tenantId,
    { query, from: timeFrom, to: timeTo, step },
    panel.query_type === 'promql' && panel.visible,
  )

  const logResult = useLogQuery(
    tenantId,
    { query, from: timeFrom, to: timeTo },
    panel.query_type === 'logql' && panel.visible,
  )

  if (!panel.visible) return null

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius)',
      padding: '12px 14px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={labelStyle}>{panel.title}</span>
        {(onEdit || onDelete) && (
          <div style={{ display: 'flex', gap: 4 }}>
            {onEdit && (
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={onEdit}>
                Edit
              </button>
            )}
            {onDelete && (
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px', color: 'var(--color-error)' }} onClick={onDelete}>
                ×
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      {panel.query_type === 'promql' && (panel.panel_type === 'timeseries' || panel.panel_type === 'gauge') && (
        <>
          {promResult.isLoading && <LoadingPlaceholder height={height} />}
          {promResult.isError && <ErrorPlaceholder height={height} msg={String(promResult.error)} />}
          {promResult.data && (
            <TimeseriesChart
              results={promResult.data.data?.result ?? []}
              unit={panel.unit}
              height={height}
            />
          )}
        </>
      )}

      {panel.query_type === 'promql' && panel.panel_type === 'stat' && (
        <>
          {promResult.isLoading && <LoadingPlaceholder height={60} />}
          {promResult.data && (
            <StatValue
              results={promResult.data.data?.result ?? []}
              unit={panel.unit}
            />
          )}
        </>
      )}

      {panel.query_type === 'logql' && (
        <>
          {logResult.isLoading && <LoadingPlaceholder height={height} />}
          {logResult.isError && <ErrorPlaceholder height={height} msg={String(logResult.error)} />}
          {logResult.data && <LogsView entries={logResult.data} height={height} />}
        </>
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────

function LoadingPlaceholder({ height }: { height: number }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>Caricamento…</span>
    </div>
  )
}

function ErrorPlaceholder({ height, msg }: { height: number; msg: string }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: 'var(--color-error)', fontSize: 12 }}>Errore: {msg}</span>
    </div>
  )
}

function StatValue({ results, unit }: { results: { values: [number, string][] }[]; unit: string }) {
  const last = results[0]?.values?.at(-1)?.[1]
  const val = last != null ? parseFloat(last) : NaN

  let display = '--'
  if (!isNaN(val)) {
    switch (unit) {
      case 'percent': display = val.toFixed(1) + '%'; break
      case 'bytes':   display = formatBytes(val); break
      case 'Bps':     display = formatBytes(val) + '/s'; break
      default:        display = val.toFixed(2)
    }
  }

  return (
    <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--color-text)', padding: '8px 0' }}>
      {display}
    </div>
  )
}

function LogsView({ entries, height }: { entries: LogEntry[]; height: number }) {
  return (
    <div style={{
      height,
      overflowY: 'auto',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      lineHeight: 1.6,
    }}>
      {entries.length === 0 && (
        <span style={{ color: 'var(--color-text-muted)' }}>Nessun log</span>
      )}
      {entries.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--color-border)', padding: '2px 0' }}>
          <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
            {new Date(e.ts).toLocaleTimeString()}
          </span>
          <span style={{ color: 'var(--color-text)' }}>{e.msg}</span>
        </div>
      ))}
    </div>
  )
}

function formatBytes(b: number): string {
  if (b >= 1_073_741_824) return (b / 1_073_741_824).toFixed(1) + ' GB'
  if (b >= 1_048_576)     return (b / 1_048_576).toFixed(1) + ' MB'
  if (b >= 1_024)         return (b / 1_024).toFixed(1) + ' KB'
  return b.toFixed(0) + ' B'
}
